import {
  objectToQueryString,
  setQueryStringParam,
  getQueryStringParams,
  deleteQueryStringParam,
  parseIdsFromQueryParam,
  sanitizeIds,
} from "../../shared/utils";
import { teamTreeExpansionMixin } from "../mixins/teamTreeExpansion";

// ==============================================================================
// PURE HELPER FUNCTIONS
// These functions have no side effects and can be tested independently
// ==============================================================================

/**
 * Returns a Set of snapshot_squad_ids that match the completion tab search, including
 * ancestor chains, or null when the search is inactive (< 3 chars).
 * @pure
 */
function computeCompletionSearchMatchedIds(teams, searchTermRaw) {
  const searchTerm = (searchTermRaw || "").trim().toLowerCase();
  if (searchTerm.length < 3) return null;

  const teamById = new Map(teams.map((t) => [t.snapshot_squad_id, t]));
  const matchedIds = new Set();

  teams.forEach((t) => {
    // Coalesce to empty string to avoid calling methods on undefined/null
    const nameMatches = (t.snapshot_squad_name || "")
      .toLowerCase()
      .includes(searchTerm);
    const teamLeadMatches = ((t.manager && t.manager.name) || "")
      .toLowerCase()
      .includes(searchTerm);
    if (!nameMatches && !teamLeadMatches) return;

    matchedIds.add(t.snapshot_squad_id);
    let parentId = t.parent_id;
    while (parentId && !matchedIds.has(parentId)) {
      matchedIds.add(parentId);
      const parent = teamById.get(parentId);
      parentId = parent?.parent_id || null;
    }
  });

  return matchedIds;
}

/**
 * Sorts overview data by the specified column and direction
 * @pure
 */
function sortOverviewData(data, sortColumn, sortDirection) {
  let sorted = [...data]; // Don't mutate input

  if (sortColumn === "name") {
    sorted.sort((a, b) => a.name.localeCompare(b.name));
  } else if (sortColumn === "votes") {
    sorted.sort((a, b) => {
      const rankA = a.priority_rank || Infinity;
      const rankB = b.priority_rank || Infinity;
      if (rankA === rankB) return a.name.localeCompare(b.name);
      return rankA - rankB;
    });
  } else if (sortColumn === "score") {
    sorted.sort((a, b) => {
      if (a.score == null && b.score == null)
        return a.name.localeCompare(b.name);
      if (a.score == null) return 1;
      if (b.score == null) return -1;
      if (a.score === b.score) return a.name.localeCompare(b.name);
      return a.score - b.score;
    });
  } else if (sortColumn === "score_change") {
    sorted.sort((a, b) => {
      if (a.score_change == null && b.score_change == null)
        return a.name.localeCompare(b.name);
      if (a.score_change == null) return 1;
      if (b.score_change == null) return -1;
      if (a.score_change === b.score_change)
        return a.name.localeCompare(b.name);
      return a.score_change - b.score_change;
    });
  } else {
    // Sort by triage category count
    sorted.sort((a, b) => {
      const countA = a[sortColumn] || 0;
      const countB = b[sortColumn] || 0;
      if (countA === countB) return a.name.localeCompare(b.name);
      return countA - countB;
    });
  }

  return sortDirection === "desc" ? sorted.reverse() : sorted;
}

/**
 * Sorts filtered results by the specified column and direction
 * @pure
 */
function sortFilteredResults(data, sortBy, sortReverse) {
  if (!data || data.length === 0) return [];

  let sorted = [...data];

  switch (sortBy) {
    case "team":
      sorted.sort((a, b) => {
        const aKey = (a.team_name || a.actor_name || "").toString();
        const bKey = (b.team_name || b.actor_name || "").toString();
        return aKey.localeCompare(bKey);
      });
      break;
    case "driver":
      sorted.sort((a, b) => {
        const aName = (a.driver_name || "").toString();
        const bName = (b.driver_name || "").toString();
        return aName.localeCompare(bName);
      });
      break;
    case "score":
      sorted.sort((a, b) => {
        if (a.score == null && b.score == null) {
          const aKey = (a.team_name || a.actor_name || "").toString();
          const bKey = (b.team_name || b.actor_name || "").toString();
          return aKey.localeCompare(bKey);
        }
        if (a.score == null) return 1;
        if (b.score == null) return -1;
        return a.score - b.score;
      });
      break;
    case "score_change":
      sorted.sort((a, b) => {
        if (a.score_change == null && b.score_change == null) return 0;
        if (a.score_change == null) return 1;
        if (b.score_change == null) return -1;
        return a.score_change - b.score_change;
      });
      break;
    case "comment":
      sorted.sort((a, b) => {
        const aHasComment = !!(a.comment_text && a.comment_text.trim());
        const bHasComment = !!(b.comment_text && b.comment_text.trim());
        return bHasComment - aHasComment;
      });
      break;
    default:
      sorted.sort((a, b) => {
        const aName = (a.actor_name || "").toString();
        const bName = (b.actor_name || "").toString();
        return aName.localeCompare(bName);
      });
  }

  return sortReverse ? sorted.reverse() : sorted;
}

/**
 * Sorts and filters improvements data
 */
function sortImprovements(improvements, sortColumn, sortDirection) {
  let filtered = [...improvements];

  if (sortColumn === "squad") {
    filtered.sort((a, b) => a.squad.localeCompare(b.squad));
  } else if (sortColumn === "squad_manager") {
    filtered.sort((a, b) => a.manager.name.localeCompare(b.manager.name));
  } else if (sortColumn === "factor") {
    filtered.sort((a, b) => a.factor.localeCompare(b.factor));
  } else if (sortColumn === "sentiment") {
    filtered.sort((a, b) => a.sentiment_change - b.sentiment_change);
  } else if (sortColumn === "priority") {
    filtered.sort((a, b) => -a.priority_change - -b.priority_change);
  }

  return sortDirection === "desc" ? filtered.reverse() : filtered;
}

/**
 * Calculates improvement success rate
 * @pure
 */
function calculateImprovementSuccessRate(improvements, numImprovementAreas) {
  const denominator = Math.max(numImprovementAreas, 1);
  return Math.round((improvements.length / denominator) * 100);
}

// ==============================================================================
// FACTORY FUNCTIONS
// ==============================================================================

/**
 * Creates initial state for a triage category (escalation, improvement_area, noop)
 * Eliminates duplication across the three categories
 *
 * State flags (both needed for proper spinner UX):
 * - loading: Controls whether to show spinner vs table in template
 * - dataFetched: Indicates data has been fetched from API
 *
 * When switching sections, loading=true shows spinner while Alpine renders.
 * After data available, loading=false (with requestAnimationFrame) shows table.
 */
function createTriageCategoryState() {
  return {
    loading: true,
    dataFetched: false,
    data: [],
    sortBy: "score",
    sortReverse: false,
    lastFetchParams: null,
  };
}

const TRIAGE_CATEGORIES = ["escalation", "improvement_area", "noop"];

// ==============================================================================
// MAIN COMPONENT
// ==============================================================================

export function triageResultsModal({
  triageResultsUrl,
  triageCompletionUrl,
  triageImprovementsUrl,
  filteredResultsUrl,
  exportUrl,
  triageLabels,
}) {
  const sectionFromParams = getQueryStringParams().get("triage_section");
  const validSections = ["overview", "escalation", "improvement_area", "noop"];
  const initialSection = validSections.includes(sectionFromParams)
    ? sectionFromParams
    : "overview";

  const modalTabFromParams = getQueryStringParams().get("triage_modal_tab");
  const initialModalTab =
    modalTabFromParams === "completion" ? "completion" : "results";

  return {
    // Mixin state (expandedRows, childRowsByParent, etc.) is owned by the Completion tab
    ...teamTreeExpansionMixin(),
    open: false,
    initialLoad: false,
    modalTab: initialModalTab,
    squadIds: [],
    tagIds: [],
    branchId: null,
    tab: null,
    triageLabels,
    selectedDriver: null,
    selectedSection: initialSection,
    overviewData: {
      loaded: false,
      data: [],
      numSquads: 0,
      squadsCompleted: 0,
      eligibleSquads: 0,
      sort: "votes",
      sortDir: "asc",
      lastUpdatedFilter: null,
      triageSortMenuOpen: false,
      lastFetchParams: null, // Track params used for last fetch
    },
    improvements: {
      loaded: false,
      data: {},
      sort: "squad",
      sortDir: "asc",
    },
    escalation: createTriageCategoryState(),
    improvement_area: createTriageCategoryState(),
    noop: createTriageCategoryState(),
    filteredResultsLoading: false,
    completionData: {
      loaded: false,
      loading: false,
      teams: [],
      squadsCompleted: 0,
      eligibleSquads: 0,
      searchTerm: "",
      matchedIds: null,
      matchedIdsKey: null,
      sortBy: "team",
      sortDir: "asc",
      lastFetchParams: null,
    },
    completionViewMode: "tree",
    init() {
      const queryParams = getQueryStringParams();
      const tabFromParams = queryParams.get("triage_tab"); // This is also used in app/controllers/kaizen/latest_snapshot_controller.rb
      const updateFromParams = queryParams.get("triage_updated");
      const branchFromParams = queryParams.get("triage_branch");
      const squadIdsFromParams = parseIdsFromQueryParam("squad_ids");
      const tagIdsFromParams = parseIdsFromQueryParam("tag_ids");

      if (squadIdsFromParams?.length) {
        this.squadIds = squadIdsFromParams;
      }
      if (tagIdsFromParams?.length) this.tagIds = tagIdsFromParams;
      if (branchFromParams?.length) this.branchId = branchFromParams;
      if (updateFromParams?.length)
        this.overviewData.lastUpdatedFilter = updateFromParams;

      if (tabFromParams == "responses") {
        this.openResultsModal();
      }

      if (tabFromParams == "improvements") {
        this.openResultsModal();
        this.setTab("improvements");
      }

      if (modalTabFromParams === "completion") {
        this.open = true;
        this.modalTab = "completion";
        this.fetchCompletionData();
        this.updateQueryParams();
      }
    },
    openResultsModal() {
      this.open = true;
      this.modalTab = "results";
      this.setTab("responses");
      this.updateQueryParams();
    },
    updateQueryParams() {
      setQueryStringParam("triage_tab", this.tab);
      setQueryStringParam("triage_modal_tab", this.modalTab);
      setQueryStringParam("squad_ids", this.squadIds?.join(",") || null);
      setQueryStringParam("tag_ids", this.tagIds?.join(",") || null);
      setQueryStringParam(
        "triage_updated",
        this.overviewData.lastUpdatedFilter,
      );
      setQueryStringParam("triage_branch", this.branchId);
    },
    clearTriageQueryParams() {
      deleteQueryStringParam("triage_tab");
      deleteQueryStringParam("triage_modal_tab");
      deleteQueryStringParam("squad_ids");
      deleteQueryStringParam("tag_ids");
      deleteQueryStringParam("triage_updated");
      deleteQueryStringParam("triage_branch");
      deleteQueryStringParam("triage_section");

      // Clear selected driver when modal closes
      this.selectedDriver = null;
    },
    setTab(tab) {
      if (this.tab === tab) return;

      this.tab = tab;
      this.updateQueryParams();

      if (this.tab == "improvements" && !this.improvements.loaded) {
        this.fetchImprovementsData();
      }

      if (this.tab == "responses") {
        if (!this.overviewData.loaded) {
          this.fetchOverviewData();
        }
        // if we navigate right to noop, load everything. Otherwise, load escalation and improvement_area.
        if (this.selectedSection === "noop") {
          this.fetchFilteredResultsIfNeeded();
        } else {
          this.fetchFilteredResultsIfNeeded(["escalation", "improvement_area"]);
        }
      }
    },
    refetch() {
      const currentParams = this.paramsKey;

      // Invalidate overview data if params changed
      if (this.overviewData.lastFetchParams !== currentParams) {
        this.improvements.loaded = false;
        this.overviewData.loaded = false;

        // Only fetch if we're currently viewing overview/responses tab
        if (this.tab === "responses" && this.selectedSection === "overview") {
          this.fetchOverviewData();
        }
      }

      // Invalidate completion data if params changed (tag_ids excluded)
      if (this.completionData.lastFetchParams !== this.completionParamsKey) {
        this.completionData.loaded = false;
        if (this.modalTab === "completion") {
          this.fetchCompletionData();
        }
      }

      // Invalidate all filtered triage categories if params changed
      this.invalidateFilteredTriageCategoriesIfNeeded(currentParams);

      // Fetch if currently viewing a filtered triage category
      if (this.isFilteredTriageCategory(this.selectedSection)) {
        this.fetchFilteredResultsIfNeeded();
      }
    },
    changeSquadIds(squadIds) {
      this.squadIds = sanitizeIds(squadIds);
      this.updateQueryParams();
      this.refetch();
    },
    changeTagIds(tagIds) {
      this.tagIds = sanitizeIds(tagIds);
      this.updateQueryParams();
      this.refetch();
    },
    changeBranchId(branchId) {
      this.branchId = branchId;
      this.updateQueryParams();
      this.refetch();
    },
    switchModalTab(tab) {
      if (this.modalTab === tab) return;
      this.modalTab = tab;
      this.updateQueryParams();

      if (tab === "completion" && !this.completionData.loaded) {
        this.fetchCompletionData();
      }

      if (tab === "results" && !this.overviewData.loaded) {
        this.setTab("responses");
      }
    },
    get params() {
      let paramObj = {
        squad_ids: `${this.squadIds.join(",")}`,
        tag_ids: `${this.tagIds.join(",")}`,
        triage_branch: this.branchId,
      };

      if (this.overviewData.lastUpdatedFilter && this.tab === "responses") {
        paramObj.last_updated_snapshot_id = this.overviewData.lastUpdatedFilter;
      }

      return `?${objectToQueryString(paramObj)}`;
    },
    get completionParams() {
      let paramObj = {
        squad_ids: `${this.squadIds.join(",")}`,
        triage_branch: this.branchId,
      };

      return `?${objectToQueryString(paramObj)}`;
    },
    get paramsKey() {
      return JSON.stringify({
        squadIds: [...this.squadIds].sort(),
        tagIds: [...this.tagIds].sort(),
        branchId: this.branchId,
        lastUpdatedFilter: this.overviewData.lastUpdatedFilter,
      });
    },
    get completionParamsKey() {
      return JSON.stringify({
        squadIds: [...this.squadIds].sort(),
        branchId: this.branchId,
      });
    },
    get exportUrl() {
      return `${exportUrl}${this.params}`;
    },
    get completionDownloadUrl() {
      if (!triageCompletionUrl) return "";
      const base = triageCompletionUrl.split("?")[0].replace(/\/$/, "");
      return `${base}.xlsx${this.completionParams}`;
    },
    get modalDownloadUrl() {
      return this.modalTab === "completion"
        ? this.completionDownloadUrl
        : this.exportUrl;
    },
    // Stuff for "Results" tab
    fetchOverviewData() {
      const fetchParams = this.paramsKey;
      fetch(`${triageResultsUrl}${this.params}`)
        .then((response) => response.json())
        .then((resp) => {
          this.overviewData.data = resp.results_by_driver;
          this.overviewData.numSquads = resp.num_squads;
          this.overviewData.squadsCompleted = resp.squads_completed;
          this.overviewData.eligibleSquads = resp.eligible_squads;
          this.overviewData.escalation_pct = resp.escalation_pct;
          this.overviewData.improvement_area_pct = resp.improvement_area_pct;
          this.overviewData.noop_pct = resp.noop_pct;
          this.overviewData.loaded = true;
          this.overviewData.lastFetchParams = fetchParams;
          this.initialLoad = true;
        });
    },
    get sortedOverviewData() {
      return sortOverviewData(
        this.overviewData.data,
        this.overviewData.sort,
        this.overviewData.sortDir,
      );
    },
    changeOverviewSort(col) {
      const { sort, sortDir } = this.overviewData;

      // Close triage menu if open
      this.overviewData.triageSortMenuOpen = false;

      if (sort == col) {
        this.overviewData.sortDir = sortDir == "desc" ? "asc" : "desc";
        return;
      }

      this.overviewData.sort = col;
      this.overviewData.sortDir = "asc";
    },
    isTriageSort() {
      return TRIAGE_CATEGORIES.includes(this.overviewData.sort);
    },
    toggleTriageSortMenu() {
      this.overviewData.triageSortMenuOpen =
        !this.overviewData.triageSortMenuOpen;
    },
    closeTriageSortMenu() {
      this.overviewData.triageSortMenuOpen = false;
    },
    selectTriageSort(triageCategory) {
      this.overviewData.sort = triageCategory;
      this.overviewData.sortDir = "desc";
      this.overviewData.triageSortMenuOpen = false;
    },
    changeLastUpdatedFilter(snapshotId) {
      this.overviewData.lastUpdatedFilter = snapshotId;
      this.updateQueryParams();
      this.refetch();
    },
    // Explicit section orchestration functions - called from template @click handlers
    openOverviewSection() {
      this.selectSection("overview");
    },
    openEscalationSection() {
      this.selectSection("escalation");
      this.ensureSectionDataFetched("escalation");
      this.ensureSectionRendered("escalation");
    },
    openImprovementAreaSection() {
      this.selectSection("improvement_area");
      this.ensureSectionDataFetched("improvement_area");
      this.ensureSectionRendered("improvement_area");
    },
    openNoopSection() {
      this.selectSection("noop");
      this.ensureSectionDataFetched("noop");
      this.ensureSectionRendered("noop");
    },
    // Updates which section is selected (UI state only)
    // Used for programmatic navigation (selectDriver, clearSelectedDriver)
    selectSection(section) {
      this.selectedSection = section;
      if (this.isFilteredTriageCategory(section)) {
        this[section].loading = true;
      }
      setQueryStringParam("triage_section", section);
    },
    selectDriver(driver) {
      this.selectedDriver = driver;

      // Invalidate all filtered triage categories (driver filter changes the data)
      this.invalidateFilteredTriageCategories();

      // Switch to escalation tab when driver is selected (can't show overview)
      if (!this.selectedSection || this.selectedSection === "overview") {
        this.selectSection("escalation");
      }

      // Fetch all categories since driver view shows counts for all three
      this.fetchFilteredResultsIfNeeded();
    },
    clearSelectedDriver() {
      this.selectedDriver = null;

      // Invalidate all filtered triage categories (removing driver filter changes the data)
      this.invalidateFilteredTriageCategories();

      // Switch back to overview when clearing driver selection
      this.selectSection("overview");
    },
    // Helper: Check if a section is a filtered triage category
    isFilteredTriageCategory(section) {
      return TRIAGE_CATEGORIES.includes(section);
    },

    // Ensure data is fetched for a specific section
    // Returns immediately if data already fetched or if fetch is in progress
    ensureSectionDataFetched(section) {
      if (!this.isFilteredTriageCategory(section)) return;
      if (this[section].dataFetched) return; // Already have data
      if (this.filteredResultsLoading) return; // Fetch already in progress

      this.fetchFilteredResultsIfNeeded([section]);
    },

    // Ensure a section's data is rendered in the DOM
    // With x-show, table is already in DOM, just hide spinner when data is ready
    ensureSectionRendered(section) {
      if (!this.isFilteredTriageCategory(section)) return;
      if (!this[section].dataFetched) return; // Can't render without data
      if (!this[section].loading) return; // Already rendered

      // With x-show, table is already in DOM, just hide spinner
      this[section].loading = false;
    },

    // Helper: Invalidate all filtered triage categories
    invalidateFilteredTriageCategories() {
      TRIAGE_CATEGORIES.forEach((categoryName) => {
        this[categoryName].loading = true;
        this[categoryName].dataFetched = false;
        this[categoryName].data = [];
      });
    },

    // Helper: Invalidate filtered triage categories only if params have changed
    invalidateFilteredTriageCategoriesIfNeeded(currentParams) {
      TRIAGE_CATEGORIES.forEach((triageCategory) => {
        if (this[triageCategory].lastFetchParams !== currentParams) {
          this[triageCategory].loading = true;
          this[triageCategory].dataFetched = false;
          this[triageCategory].data = [];
        }
      });
    },

    // Fetch filtered results only if needed (not already loaded and not already loading)
    fetchFilteredResultsIfNeeded(categories = TRIAGE_CATEGORIES) {
      // Check if any category needs data
      const needsLoad = categories.some(
        (triageCategory) => !this[triageCategory].dataFetched,
      );

      if (!needsLoad || this.filteredResultsLoading) return;

      this.filteredResultsLoading = true;
      const fetchParams = this.paramsKey;

      let url = `${filteredResultsUrl}${this.params}`;
      if (this.selectedDriver) {
        url += `&factor_id=${this.selectedDriver.id}`;
      }

      fetch(url)
        .then((response) => response.json())
        .then((resp) => {
          const results = resp.results || {};

          categories.forEach((triageCategory) => {
            this[triageCategory].data = results[triageCategory] || [];
            this[triageCategory].dataFetched = true;
            this[triageCategory].lastFetchParams = fetchParams;
            this[triageCategory].loading = false;
          });
        })
        .catch((error) => {
          console.error("Error fetching filtered results:", error);
          categories.forEach((triageCategory) => {
            if (!this[triageCategory].dataFetched) {
              this[triageCategory].data = [];
              this[triageCategory].dataFetched = true;
              this[triageCategory].loading = false; // Show empty state
              this[triageCategory].lastFetchParams = fetchParams;
            }
          });
        })
        .finally(() => {
          this.filteredResultsLoading = false;
        });
    },
    changeFilteredResultsSort(triageCategory, col) {
      const currentSort = this[triageCategory].sortBy;
      const currentReverse = this[triageCategory].sortReverse;

      if (currentSort === col) {
        this[triageCategory].sortReverse = !currentReverse;
      } else {
        this[triageCategory].sortBy = col;
        this[triageCategory].sortReverse = false;
      }
    },
    isFilteredResultsSortOrder(triageCategory, col) {
      return this[triageCategory].sortBy === col;
    },
    getSortedFilteredResults(triageCategory) {
      return sortFilteredResults(
        this[triageCategory].data,
        this[triageCategory].sortBy,
        this[triageCategory].sortReverse,
      );
    },

    // Stuff for "Completion" tab
    fetchCompletionData() {
      if (this.completionData.loading) return;
      this.completionData.loading = true;
      const fetchParams = this.completionParamsKey;

      fetch(`${triageCompletionUrl}${this.completionParams}`, {
        headers: { Accept: "application/json" },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((resp) => {
          if (fetchParams !== this.completionParamsKey) return;

          this.completionData.teams = resp.teams || [];
          this.completionData.squadsCompleted = resp.squads_completed;
          this.completionData.eligibleSquads = resp.eligible_squads;
          this.completionData.loaded = true;
          this.completionData.lastFetchParams = fetchParams;
          this.completionData.matchedIdsKey = null;

          // Auto-expand root parent teams
          this.expandedRows = new Set();
          this.completionData.teams.forEach((team) => {
            if (team.is_parent && !team.parent_id) {
              this.expandedRows.add(team.snapshot_squad_id);
            }
          });
          this.rebuildCompletionChildRows();
          this.syncCompletionExpandedRowsForSearch();
        })
        .catch((error) => {
          console.error("Error fetching completion data:", error);
          this.completionData.teams = [];
          this.completionData.loaded = true;
          this.completionData.lastFetchParams = fetchParams;
        })
        .finally(() => {
          this.completionData.loading = false;
          // Retry if params changed while the fetch was in flight
          if (
            this.completionData.lastFetchParams !== this.completionParamsKey &&
            this.modalTab === "completion"
          ) {
            this.fetchCompletionData();
          }
        });
    },
    toggleCompletionExpand(item) {
      const id = item.snapshot_squad_id;
      if (this.expandedRows.has(id)) {
        this.expandedRows.delete(id);
      } else {
        this.expandedRows.add(id);
      }
      // Trigger Alpine reactivity
      this.expandedRows = new Set(this.expandedRows);
    },
    autoExpandMatchedParents(matchedIds, teams) {
      if (!matchedIds || !teams?.length) return;

      const next = new Set(this.expandedRows);
      teams.forEach((t) => {
        if (t.is_parent && matchedIds.has(t.snapshot_squad_id)) {
          next.add(t.snapshot_squad_id);
        }
      });
      this.expandedRows = next;
    },
    completionMatchedIds() {
      const key = this.completionData.searchTerm;
      if (this.completionData.matchedIdsKey === key) {
        return this.completionData.matchedIds;
      }
      const result = computeCompletionSearchMatchedIds(
        this.completionData.teams,
        this.completionData.searchTerm,
      );
      this.completionData.matchedIds = result;
      this.completionData.matchedIdsKey = key;
      return result;
    },
    syncCompletionExpandedRowsForSearch() {
      const teams = this.completionData.teams;
      if (!teams?.length) return;

      this.rebuildCompletionChildRows();
      const matchedIds = this.completionMatchedIds();
      if (matchedIds) {
        this.autoExpandMatchedParents(matchedIds, teams);
      }
    },
    rebuildCompletionChildRows() {
      const teams = this.completionData.teams;
      const matchedIds = this.completionMatchedIds();
      const filteredTeams = matchedIds
        ? teams.filter((t) => matchedIds.has(t.snapshot_squad_id))
        : teams;
      const filteredIds = new Set(
        filteredTeams.map((t) => t.snapshot_squad_id),
      );

      const byParent = {};
      filteredTeams.forEach((t) => {
        if (t.parent_id && filteredIds.has(t.parent_id)) {
          if (!byParent[t.parent_id]) byParent[t.parent_id] = [];
          byParent[t.parent_id].push(t);
        }
      });
      this.childRowsByParent = byParent;
    },
    get completionTreeItems() {
      const teams = this.completionData.teams;
      if (!teams || teams.length === 0) return [];

      const matchedIds = this.completionMatchedIds();
      const filteredTeams = matchedIds
        ? teams.filter((t) => matchedIds.has(t.snapshot_squad_id))
        : teams;

      // Leaf teams mode: flat list of non-parent teams only
      if (this.completionViewMode === "leaf") {
        const leafTeams = filteredTeams.filter((t) => !t.is_parent);
        return this.sortCompletionTeams(leafTeams).map((t) => ({
          ...t,
          _depth: 0,
        }));
      }

      // Tree mode: childRowsByParent is pre-built by rebuildCompletionChildRows
      const filteredIds = new Set(
        filteredTeams.map((t) => t.snapshot_squad_id),
      );
      const roots = this.sortCompletionTeams(
        filteredTeams.filter(
          (t) => !t.parent_id || !filteredIds.has(t.parent_id),
        ),
      );
      return this.hydrateDataWithNestedRows(
        roots,
        this.completionSortFunction(),
      );
    },
    completionSortFunction() {
      const { sortBy, sortDir } = this.completionData;
      return (a, b) => {
        if (a.is_parent !== b.is_parent) return b.is_parent - a.is_parent;

        let cmp = 0;
        switch (sortBy) {
          case "team":
            cmp = a.snapshot_squad_name.localeCompare(b.snapshot_squad_name);
            break;
          case "completion":
            // Parent-vs-leaf case is handled by the early return above
            if (a.is_parent && b.is_parent) {
              const aPct =
                a.eligible_count > 0 ? a.completed_count / a.eligible_count : 0;
              const bPct =
                b.eligible_count > 0 ? b.completed_count / b.eligible_count : 0;
              cmp = aPct - bPct;
            } else {
              cmp = (a.is_complete ? 1 : 0) - (b.is_complete ? 1 : 0);
            }
            if (cmp === 0)
              cmp = a.snapshot_squad_name.localeCompare(b.snapshot_squad_name);
            break;
          case "team_lead":
            cmp = (a.manager?.name || "").localeCompare(b.manager?.name || "");
            if (cmp === 0)
              cmp = a.snapshot_squad_name.localeCompare(b.snapshot_squad_name);
            break;
          default:
            cmp = a.snapshot_squad_name.localeCompare(b.snapshot_squad_name);
        }

        return sortDir === "desc" ? -cmp : cmp;
      };
    },
    sortCompletionTeams(teams) {
      return [...teams].sort(this.completionSortFunction());
    },
    changeCompletionSort(col) {
      if (this.completionData.sortBy === col) {
        this.completionData.sortDir =
          this.completionData.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.completionData.sortBy = col;
        this.completionData.sortDir = "asc";
      }
    },
    completionPct(completed, eligible) {
      return eligible > 0 ? Math.round((completed / eligible) * 100) : 0;
    },
    clearCompletionSearch() {
      this.completionData.searchTerm = "";
      // Restore default expansion (root parents only)
      this.expandedRows = new Set();
      this.completionData.teams.forEach((team) => {
        if (team.is_parent && !team.parent_id) {
          this.expandedRows.add(team.snapshot_squad_id);
        }
      });
      this.rebuildCompletionChildRows();
    },
    highlightCompletionSearch(text) {
      if (!text) return "";
      const searchTerm = (this.completionData.searchTerm || "").trim();
      if (searchTerm.length < 3) return window.scrubHtml(text);

      const searchLower = searchTerm.toLowerCase();
      const textLower = text.toLowerCase();
      const index = textLower.indexOf(searchLower);

      if (index === -1) return window.scrubHtml(text);

      const before = text.substring(0, index);
      const match = text.substring(index, index + searchTerm.length);
      const after = text.substring(index + searchTerm.length);

      return `${window.scrubHtml(before)}<span class="bg-amber-100">${window.scrubHtml(match)}</span>${window.scrubHtml(after)}`;
    },

    // teamTreeExpansionMixin overrides — completion tab loads all data upfront
    getChildDataEndpoint() {
      return "";
    },
    getChildDataParams() {
      return {};
    },
    getCachedSquads() {
      return this.completionData.teams || [];
    },

    // Stuff for "Improvements" tab
    fetchImprovementsData() {
      fetch(`${triageImprovementsUrl}${this.params}`)
        .then((response) => response.json())
        .then((resp) => {
          this.improvements.data = resp;
          this.improvements.loaded = true;
        });
    },
    get improvementAreaSuccessRate() {
      const { improvements = [], num_improvement_areas = 0 } =
        this.improvements.data;
      return calculateImprovementSuccessRate(
        improvements,
        num_improvement_areas,
      );
    },
    get sortedImprovements() {
      return sortImprovements(
        this.improvements.data.improvements,
        this.improvements.sort,
        this.improvements.sortDir,
      );
    },
    changeImprovementsSort(col) {
      const { sort, sortDir } = this.improvements;

      if (sort == col) {
        this.improvements.sortDir = sortDir == "desc" ? "asc" : "desc";
        return;
      }

      this.improvements.sort = col;
      this.improvements.sortDir = "desc";
    },
    percentageOfResponses(item, positionCount) {
      if (item.count === 0) return "0%";
      return `${Math.round((100 * item[positionCount]) / item.count)}%`;
    },
  };
}
